import { isMediaReference } from "./previewMedia";
/** Backend / Mongo often store 0/1 integers; older UI used "1" strings. */
function isMediaShown(v: any): boolean {
  if (v === false || v === 0 || v === "0") return false;
  return true;
}

function isMediaPinned(v: any): boolean {
  return v === true || v === 1 || v === "1";
}

function resolveMediaUrl(ele: any): string {
  const u = ele?.mediaURL ?? ele?.mediaUrl ?? "";
  return typeof u === "string" ? u.trim() : "";
}

/** Use pathname for extension so presigned URLs (?query) still match .jpg / .mp4. */
function extensionFromUrl(url: string): string {
  if (!url) return "";
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    const i = pathname.lastIndexOf(".");
    return i >= 0 ? pathname.slice(i) : "";
  } catch {
    const path = url.split("?")[0].toLowerCase();
    const i = path.lastIndexOf(".");
    return i >= 0 ? path.slice(i) : "";
  }
}

function mapExtensionToType(ext: string): string | null {
  const e = ext.toLowerCase();
  const map: Record<string, string> = {
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".doc": "application/msword",
    ".pdf": "application/pdf",
    ".jpg": "image/jpg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".bmp": "image/bmp",
    ".mp4": "video/mp4",
    ".webm": "video/webm",
    ".ogg": "video/ogg",
  };
  return map[e] ?? null;
}

function inferTypeWhenNoExtension(url: string): string {
  const low = url.toLowerCase();
  if (low.includes(".mp4") || low.includes(".webm") || low.includes(".ogg")) {
    if (low.includes(".webm")) return "video/webm";
    if (low.includes(".ogg")) return "video/ogg";
    return "video/mp4";
  }
  if (low.includes("video") || /format[=:]mp4/i.test(low)) return "video/mp4";
  return "image/jpeg";
}

function withCaptureMeta(row: any, ele: any) {
  if (ele?.mediaId) row.mediaId = ele.mediaId;
  if (ele?.mediaAssetId) row.mediaAssetId = ele.mediaAssetId;
  if (ele?.source) row.source = ele.source;
  if (ele?.lat != null) row.lat = ele.lat;
  if (ele?.lng != null) row.lng = ele.lng;
  if (ele?.accuracyM != null) row.accuracyM = ele.accuracyM;
  if (ele?.uploadByName) row.uploadByName = ele.uploadByName;
  if (ele?.uploadBy) row.uploadBy = ele.uploadBy;
  if (ele?.kind) row.kind = ele.kind;
  if (ele?.view) row.view = String(ele.view).toUpperCase();
  // Small copies the list API adds for old S3 photos; tiles render these instead of the full photo.
  if (ele?.mediaUrlThumb) row.mediaUrlThumb = ele.mediaUrlThumb;
  if (ele?.mediaUrlCard) row.mediaUrlCard = ele.mediaUrlCard;
  return row;
}

function pushFromExtension(
  base64Files: any[],
  url: string,
  ext: string,
  withPin: boolean,
  ele: any
) {
  const mime = mapExtensionToType(ext);
  if (!mime) return;
  const shown = isMediaShown(ele?.show);
  const pinned = isMediaPinned(ele?.pin);
  if (withPin) {
    base64Files.push(
      withCaptureMeta(
        {
          type: mime,
          blobImage: url,
          show: shown,
          pin: pinned,
        },
        ele
      )
    );
  } else {
    base64Files.push(
      withCaptureMeta(
        {
          type: mime,
          blobImage: url,
          show: shown,
        },
        ele
      )
    );
  }
}

function pushUnknownHttp(
  base64Files: any[],
  url: string,
  withPin: boolean,
  ele: any
) {
  // An hkp-media: reference the server could not sign (media service off, photo not ready) is still a
  // saved photo: keep the row so saving the product does not erase it.
  if (!/^https?:\/\//i.test(url) && !isMediaReference(url)) return;
  const mime = inferTypeWhenNoExtension(url);
  const shown = isMediaShown(ele?.show);
  const pinned = isMediaPinned(ele?.pin);
  if (withPin) {
    base64Files.push(
      withCaptureMeta(
        {
          type: mime,
          blobImage: url,
          show: shown,
          pin: pinned,
        },
        ele
      )
    );
  } else {
    base64Files.push(
      withCaptureMeta(
        {
          type: mime,
          blobImage: url,
          show: shown,
        },
        ele
      )
    );
  }
}

const baseImageCovert = (base: any) => {
  const base64Files: any[] = [];
  if (!Array.isArray(base)) return base64Files;
  base.forEach((ele: any) => {
    const url = resolveMediaUrl(ele);
    if (!url) return;
    const ext = extensionFromUrl(url);
    if (ext && mapExtensionToType(ext)) {
      pushFromExtension(base64Files, url, ext, false, ele);
    } else {
      pushUnknownHttp(base64Files, url, false, ele);
    }
  });
  return base64Files;
};

const baseImageCovertForLogoPin = (base: any) => {
  const base64Files: any[] = [];
  if (!Array.isArray(base)) return base64Files;
  base.forEach((ele: any) => {
    const url = resolveMediaUrl(ele);
    if (!url) return;
    const ext = extensionFromUrl(url);
    if (ext && mapExtensionToType(ext)) {
      pushFromExtension(base64Files, url, ext, true, ele);
    } else {
      pushUnknownHttp(base64Files, url, true, ele);
    }
  });
  return base64Files;
};

export { baseImageCovert, baseImageCovertForLogoPin };
